import { useEffect, useRef, useState } from 'react'
import { hasApiErrorCode } from '../lib/utils'
import { PaginationMeta } from '../types/PaginationMeta'
import useApi from './useApi'
import usePagination from './usePagination'

type QueryValue = string | number | boolean | undefined

interface PaginatedResponse {
	meta: PaginationMeta
}

interface Options<TResponse, TRow> {
	path: string
	/** Filters, sort and scope. Compared by value; a change resets to page 1 and refetches once. */
	query?: Record<string, QueryValue>
	pageSize?: number
	getRows: (response: TResponse) => TRow[]
	enabled?: boolean
	/** Silent background refresh (no loading state); paused while the tab is hidden. */
	pollIntervalMs?: number
}

const NO_ROWS: never[] = []

/**
 * The single owner of list-page paging: builds the request, ignores stale responses, tracks
 * loading/error, and applies the response `meta` to the pagination state so callers cannot
 * forget to. Expects the backend list shape `{ ...rows, meta }` under `response.data`.
 */
function usePaginatedFetch<TResponse extends PaginatedResponse, TRow = unknown>({
	path,
	query = {},
	pageSize = 20,
	getRows,
	enabled = true,
	pollIntervalMs,
}: Options<TResponse, TRow>) {
	const api = useApi()
	const paging = usePagination(1, pageSize)
	const { current_page: currentPage, per_page: perPage } = paging.pagination

	const [response, setResponse] = useState<{ api: typeof api; data: TResponse } | null>(null)
	const [loading, setLoading] = useState(enabled)
	const [error, setError] = useState<{ api: typeof api; value: unknown } | null>(null)
	const [refreshToken, setRefreshToken] = useState(0)
	const activeFetch = useRef<AbortController | null>(null)
	const activePoll = useRef<AbortController | null>(null)

	const queryKey = JSON.stringify(query)
	const [previousQueryKey, setPreviousQueryKey] = useState(queryKey)
	const [previousApi, setPreviousApi] = useState(api)
	if (api !== previousApi) {
		setPreviousApi(api)
		paging.onPageChange(1)
	}
	if (queryKey !== previousQueryKey) {
		// Adjusting state during render re-renders before effects run, so only one fetch fires.
		setPreviousQueryKey(queryKey)
		paging.onPageChange(1)
	}

	const fetchOnce = async (signal: AbortSignal): Promise<TResponse> => {
		const params = new URLSearchParams()
		Object.entries(query).forEach(([key, value]) => {
			if (value !== undefined) {
				params.set(key, String(value))
			}
		})
		params.set('page', String(currentPage))
		params.set('perPage', String(perPage))
		const result = await api.get(`${path}?${params.toString()}`, { signal })
		return result.data as TResponse
	}

	useEffect(() => {
		if (!enabled) {
			setLoading(false)
			return
		}
		let cancelled = false
		activePoll.current?.abort()
		activePoll.current = null
		const controller = new AbortController()
		activeFetch.current = controller
		setLoading(true)
		fetchOnce(controller.signal)
			.then((data) => {
				if (cancelled || controller.signal.aborted) {
					return
				}
				setResponse({ api, data })
				paging.readMeta(data.meta)
				setError(null)
				setLoading(false)
			})
			.catch((err) => {
				if (cancelled || controller.signal.aborted) {
					return
				}
				setResponse(null)
				setError({ api, value: err })
				setLoading(false)
			})
			.finally(() => {
				if (activeFetch.current === controller) activeFetch.current = null
			})
		return () => {
			cancelled = true
			controller.abort()
			if (activeFetch.current === controller) activeFetch.current = null
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, enabled, path, queryKey, currentPage, perPage, refreshToken])

	useEffect(() => {
		if (!enabled || !pollIntervalMs) {
			return
		}
		let cancelled = false
		const poll = async () => {
			if (document.visibilityState !== 'visible' || activeFetch.current || activePoll.current) {
				return
			}
			const controller = new AbortController()
			activePoll.current = controller
			try {
				const data = await fetchOnce(controller.signal)
				if (!cancelled && !controller.signal.aborted) {
					setResponse({ api, data })
					paging.readMeta(data.meta)
					setError(null)
				}
			} catch (err) {
				if (!cancelled && !controller.signal.aborted && (hasApiErrorCode(err, 'ACCESS_AUDIT_UNAVAILABLE') || hasApiErrorCode(err, 'READ_ONLY_ACCESS'))) {
					setResponse(null)
					setError({ api, value: err })
				}
			} finally {
				if (activePoll.current === controller) activePoll.current = null
			}
		}
		const interval = setInterval(poll, pollIntervalMs)
		document.addEventListener('visibilitychange', poll)
		return () => {
			cancelled = true
			activePoll.current?.abort()
			activePoll.current = null
			clearInterval(interval)
			document.removeEventListener('visibilitychange', poll)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, enabled, pollIntervalMs, path, queryKey, currentPage, perPage])

	const currentResponse = response?.api === api ? response.data : null
	return {
		rows: currentResponse ? getRows(currentResponse) : (NO_ROWS as TRow[]),
		/** Raw response, for extra fields returned alongside the rows (e.g. summaries). */
		response: currentResponse,
		loading,
		error: error?.api === api ? error.value : null,
		pagination: currentResponse
			? paging.pagination
			: { ...paging.pagination, total: 0, first_page: 1, last_page: Math.max(1, currentPage) },
		onPageChange: paging.onPageChange,
		refetch: () => setRefreshToken((token) => token + 1),
	}
}

export default usePaginatedFetch
